import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SiteConfigProvider } from "@/context/SiteConfigContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { useSitewideCatMode } from "@/lib/catMode";
import CatModeToggle from "@/components/CatModeToggle";

// Keep the large analytics workspaces out of the initial bundle. Browsers now
// download and parse only the page being opened instead of the entire site.
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const AuthSuccess = lazy(() => import("@/pages/AuthSuccess"));
const Demo = lazy(() => import("@/pages/Demo"));
const Heroes = lazy(() => import("@/pages/Heroes"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Players = lazy(() => import("@/pages/Players"));
const PlayerDetail = lazy(() => import("@/pages/PlayerDetail"));
const MatchDetail = lazy(() => import("@/pages/MatchDetail"));
const Teams = lazy(() => import("@/pages/Teams"));
const TeamDetail = lazy(() => import("@/pages/TeamDetail"));
const TeamHeroDetail = lazy(() => import("@/pages/TeamHeroDetail"));
const TeamPlayer = lazy(() => import("@/pages/TeamPlayer"));
const SharedTeam = lazy(() => import("@/pages/SharedTeam"));

const RouteFallback = () => <div className="flex min-h-[45vh] items-center justify-center"><div className="text-xs uppercase tracking-label text-muted-foreground">Loading view…</div></div>;

const Shell = ({ children }) => <Layout>{children}</Layout>;
const SitewideCatMode = () => { useSitewideCatMode(); return null; };
const StandaloneCatModeToggle = () => {
  const { pathname } = useLocation();
  const usesMainLayout = /^\/(demo|heroes|leaderboard|players|matches|teams)(\/|$)/.test(pathname);
  return usesMainLayout ? null : <CatModeToggle floating />;
};

class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-background px-6 py-8 text-foreground">
          <div className="mx-auto max-w-2xl rounded-lg border border-red-500/30 bg-red-950/10 px-5 py-6">
            <div className="text-sm font-bold text-red-200">The app hit a rendering error.</div>
            <div className="mt-2 font-mono text-xs text-red-100/70">{this.state.error.message}</div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:border-brass/60"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <RootErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <SiteConfigProvider>
            <AuthProvider>
              <SitewideCatMode />
              <StandaloneCatModeToggle />
              <Suspense fallback={<RouteFallback />}><Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/success" element={<AuthSuccess />} />
                <Route path="/demo" element={<Shell><Demo /></Shell>} />
                <Route path="/heroes" element={<Shell><Heroes /></Shell>} />
                <Route path="/leaderboard" element={<Shell><Leaderboard /></Shell>} />
                <Route path="/players" element={<Shell><Players /></Shell>} />
                <Route path="/players/:accountId" element={<Shell><PlayerDetail /></Shell>} />
                <Route path="/matches/:matchId" element={<Shell><MatchDetail /></Shell>} />
                <Route path="/teams" element={<Shell><Teams /></Shell>} />
                <Route path="/teams/:id/heroes/:heroId/:view" element={<Shell><TeamHeroDetail /></Shell>} />
                <Route path="/teams/:id/heroes/:heroId" element={<Shell><TeamHeroDetail /></Shell>} />
                <Route path="/teams/:id/players/:accountId" element={<Shell><TeamPlayer /></Shell>} />
                <Route path="/teams/:id/:section" element={<Shell><TeamDetail /></Shell>} />
                <Route path="/teams/:id" element={<Shell><TeamDetail /></Shell>} />
                <Route path="/share/:shareId" element={<SharedTeam />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes></Suspense>
              <Toaster position="top-right" theme="dark" />
            </AuthProvider>
          </SiteConfigProvider>
        </BrowserRouter>
      </div>
    </RootErrorBoundary>
  );
}

export default App;
